import styled, { css } from 'styled-components';
import { Swiper } from 'swiper/react';
import type { SwiperOptions } from 'swiper/types/swiper-options';
import type { Maybe } from 'yup';

import { Container } from '@/components/Container';
import { Grid } from '@/components/Grid';
import { theme } from '@/theme/getTheme';

import {
  calcSwiperSlideBetweenByBreakpoints,
  calcSwiperSlideMaxWidth,
  calcSwiperSlideMaxWidthByBreakpoints,
  calcSwiperSlideSpaceBetween,
} from './helper';

import { visibilityControl } from '../Card/Card.styles';

export const Wrapper = styled(Container)<{
  $visibleInDesktop?: Maybe<boolean>;
  $visibleInMobile?: Maybe<boolean>;
  full: boolean;
}>`
  &&& {
    .TextBaseCardContent {
      width: 100%;
    }
    ${theme.media.md.up} {
      .MediaAndBottomTextBaseCardContent {
        justify-content: flex-start;
      }
    }
    ${theme.media.lg.up} {
      .MediaAndBottomTextBaseCardContent {
        justify-content: flex-start;
      }
    }
  }
  ${({ $visibleInDesktop, $visibleInMobile }) => css`
    ${visibilityControl($visibleInDesktop, $visibleInMobile)}
  `};
  ${({ full }) => css`
    ${full &&
    css`
      max-width: 100% !important;
    `}
  `}
`;

const calcContentAlignment = (
  col: number,
  itemsCount: number,
  contentAlignment = 'Left',
) => {
  const nthChild = itemsCount % col;
  const centerOffset = (col - nthChild) * 1 + 1;
  const rightOffset = (col - nthChild) * 2 + 1;
  if (contentAlignment === 'Center') {
    return `&:nth-last-child(${nthChild}) {
        grid-column: ${centerOffset} / span 2;
      }`;
  } else if (contentAlignment === 'Right') {
    return `&:nth-last-child(${nthChild}) {
        grid-column: ${rightOffset} / span 2;
      }`;
  } else {
    return '';
  }
};

export const StyledGrid = styled(Grid)<{
  $backgroundColor?: string;
  $col: number;
  $colForMobile: number;
  $contentAlignment?: string | null;
  $itemsCount: number;
  $reverseForMobile?: boolean;
}>`
  ${({
    $backgroundColor,
    $col,
    $colForMobile,
    $contentAlignment,
    $itemsCount,
    $reverseForMobile,
    theme,
  }) => css`
    grid-template-columns: repeat(${2 * $col}, 1fr);
    ${theme.media.md.down} {
      grid-template-columns: repeat(${2 * $colForMobile}, 1fr);
      ${$reverseForMobile &&
      css`
        display: flex;
        flex-direction: column-reverse;
        flex-wrap: wrap;
      `}
    }
    > * {
      ${theme.media.md.up} {
        ${calcContentAlignment($col, $itemsCount, $contentAlignment ?? 'Left')}
      }
      ${theme.media.md.down} {
        ${calcContentAlignment(
          $colForMobile,
          $itemsCount,
          $contentAlignment ?? 'Left',
        )}
      }
      grid-column: span 2;
      overflow: hidden;
      ${$backgroundColor &&
      css`
        background: ${$backgroundColor};
      `}
    }
  `}
`;

export const StyledSwiper = styled(Swiper)<{
  $backgroundColor?: string;
  $hideNavigation: boolean;
  breakpoints: SwiperOptions['breakpoints'];
  separator: string;
  slidesPerView: number | 'auto';
  spaceBetween?: string | number;
}>`
  .swiper-wrapper {
    .swiper-slide {
      ${({ breakpoints, slidesPerView, spaceBetween }) => css`
        ${calcSwiperSlideMaxWidth({ slidesPerView, spaceBetween })}
        ${calcSwiperSlideMaxWidthByBreakpoints(breakpoints)}
        ${calcSwiperSlideSpaceBetween({ spaceBetween })}
        ${calcSwiperSlideBetweenByBreakpoints(breakpoints)}
      `}
    }
  }
  &.swiper {
    > .swiper-button {
      &-prev,
      &-next {
        display: none;
      }

      &-prev.swiper-button-disabled,
      &-next.swiper-button-disabled {
        display: none;
      }
    }
    ${({ theme }) => css`
      ${theme.media.md.down} {
        min-height: unset;
      }

      ${theme.mediaQueries.md} {
        > .swiper-button {
          &-prev,
          &-next {
            margin-top: 0;
            color: ${theme.colors.black};
            display: initial;
            top: calc(50% - 20px);
            width: 40px;
            height: 40px;
            justify-content: center;
            align-items: center;
            display: inline-flex;
            ::after {
              font-size: 24px;
            }
          }

          &-prev {
            left: 0;
            text-align: left;
          }

          &-next {
            right: 0;
            text-align: right;
          }
        }
      }
    `}

    ${({ $backgroundColor, $hideNavigation, theme }) => css`
      ${$hideNavigation &&
      css`
        .swiper-button-prev,
        .swiper-button-next {
          display: none;
        }
      `}

      .swiper-slide {
        ${$backgroundColor &&
        css`
          background: ${$backgroundColor};
        `}
        height: auto;
      }

      .swiper-scrollbar {
        background: ${({ theme }) => theme.colors.mediumGrey};
        height: 2px;
        bottom: 0;
        width: 50%;
        left: 25%;
        ${theme.media.md.down} {
          width: calc(100% - 2rem);
          left: 1rem;
        }

        .swiper-drag {
          background: ${({ theme }) => theme.colors.primary};
        }
      }
    `}
  }
`;
